import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { MdModeEdit } from 'react-icons/md';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { Toaster, toast } from 'react-hot-toast';
import Toggle from 'react-toggle';
import { apiCall } from 'utils/apiClient';
import Pagination from 'components/pagination';

const normalizeList = (payload, key) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.[key])) return payload[key];
    return [];
};

const ListServices = () => {
    const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;
    const deletstyle = { color: "red" };
    const editstyle = { color: "green" };
    const navigate = useNavigate();

    const [serviceData, setServiceData] = useState([]);
    const [serviceTypeData, setServiceTypeData] = useState([]);
    const [Loader, setLoader] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    const getAllServices = useCallback(async () => {
        try {
            const result = await apiCall({
                endpoint: "services",
                method: 'GET',
            });

            if (result?.status === 200) {
                setServiceData(normalizeList(result?.data, 'services'));
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            toast.error("Failed to fetch services");
        }
    }, []);

    const getAllServiceTypes = useCallback(async () => {
        try {
            const result = await apiCall({
                endpoint: "services-type",
                method: 'GET',
            });

            if (result?.status === 200) {
                setServiceTypeData(normalizeList(result?.data, 'services_type'));
            }
        } catch (error) {
            console.error('Error fetching service types:', error);
        }
    }, []);

    useEffect(() => {
        const loadInitialData = async () => {
            setLoader(true);
            await Promise.all([getAllServices(), getAllServiceTypes()]);
            setLoader(false);
        };

        loadInitialData();
    }, [getAllServices, getAllServiceTypes]);

    const selectedCategoryName = useMemo(() => {
        if (!selectedCategory) return '';
        const selected = serviceTypeData.find((item) => String(item?.id) === selectedCategory);
        return String(selected?.name || '').toLowerCase();
    }, [serviceTypeData, selectedCategory]);

    const filteredServices = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return serviceData.filter((service) => {
            const serviceName = String(service?.name || '').toLowerCase();
            const serviceTypeName = String(service?.service_name || service?.service_type_name || '').toLowerCase();
            const serviceTypeId = String(
                service?.service_type_id ?? service?.serviceTypeId ?? service?.service_type?.id ?? ''
            );

            const matchesSearch =
                !normalizedSearch ||
                serviceName.includes(normalizedSearch) ||
                serviceTypeName.includes(normalizedSearch);

            const matchesCategory =
                !selectedCategory ||
                serviceTypeId === selectedCategory ||
                (selectedCategoryName && serviceTypeName === selectedCategoryName);

            return matchesSearch && matchesCategory;
        });
    }, [serviceData, searchTerm, selectedCategory, selectedCategoryName]);

    const totalRecords = filteredServices.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const paginatedServices = filteredServices.slice(startIndex, endIndex);
    const displayedCount = totalRecords === 0 ? 0 : Math.min(totalRecords, endIndex);

    const handleServiceDelete = async (id) => {
        try {
            const result = await apiCall({
                endpoint: `/services/delete/${id}`,
                method: 'DELETE',
            });

            if (result?.status === 200) {
                await getAllServices();
                toast.success("Services Deleted Successfully");
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            toast.error("Failed to delete service");
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === "1" ? "0" : "1";

        try {
            const result = await apiCall({
                endpoint: `services/change-status/${id}`,
                method: "PUT",
                data: { status: newStatus },
            });

            if (result?.status === 200) {
                setServiceData((prevData) =>
                    prevData.map((item) =>
                        item.id === id ? { ...item, status: newStatus } : item
                    )
                );
            } else {
                console.error("Error updating status:", result?.message);
            }
        } catch (error) {
            console.error("Request failed:", error);
            toast.error("Failed to update status");
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Toaster position="top-center" reverseOrder={false} />

            {Loader ? (
                <div className='flex justify-center items-center h-[75vh] w-full'>
                    <Spin indicator={antIcon} />
                </div>
            ) : (
                <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div style={{ fontSize: 15 }} className="px-6 pt-5 font-semibold text-gray-800">
                        Services
                    </div>

                    <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="w-full lg:w-auto">
                                <input
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:w-96 px-3 py-2"
                                    placeholder="Search Services"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <button
                                onClick={() => navigate('/services/create')}
                                className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg"
                            >
                                Add New Service
                            </button>
                        </div>

                        <div className="mt-3 flex flex-col gap-2 xl:flex-row xl:items-center">
                            <div className="w-full xl:w-56 shrink-0">
                                <select
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2"
                                    value={selectedCategory}
                                    onChange={(e) => {
                                        setSelectedCategory(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="">All Categories</option>
                                    {serviceTypeData.map((serviceType) => (
                                        <option key={serviceType.id} value={String(serviceType.id)}>
                                            {serviceType.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-full rounded-md border border-gray-100">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    recordsPerPage={recordsPerPage}
                                    onRecordsPerPageChange={(val) => {
                                        setRecordsPerPage(val);
                                        setCurrentPage(1);
                                    }}
                                    totalRecords={totalRecords}
                                    count={displayedCount}
                                    showFirstLastControls
                                    compact
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto relative my-4 mx-4">
                        <table className="w-full text-xs text-left text-gray-600">
                            <thead className="text-[11px] text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="py-2.5 px-3">Sr No</th>
                                    <th className="py-2.5 px-3">Name</th>
                                    <th className="py-2.5 px-3">Service Name</th>
                                    <th className="py-2.5 px-3">Rate</th>
                                    <th className="py-2.5 px-3">Rate Type</th>
                                    <th className="py-2.5 px-3">Status</th>
                                    <th className="py-2.5 px-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedServices.length > 0 ? (
                                    paginatedServices.map((el, index) => (
                                        <tr className="bg-white border-b hover:bg-gray-50" key={el.id}>
                                            <td className="py-2 px-3 whitespace-nowrap">{startIndex + index + 1}</td>
                                            <td className="py-2 px-3">{el.name}</td>
                                            <td className="py-2 px-3">{el.service_name}</td>
                                            <td className="py-2 px-3 whitespace-nowrap">{el.rate}</td>
                                            <td className="py-2 px-3 whitespace-nowrap">{el.rate_type}</td>
                                            <td className="py-2 px-3">
                                                <Toggle
                                                    checked={el.status === "1"}
                                                    onChange={() => handleToggleStatus(el.id, el.status)}
                                                    icons={false}
                                                    className="scale-90"
                                                />
                                            </td>
                                            <td className="py-2 px-3">
                                                <div className="flex items-center">
                                                    <MdModeEdit
                                                        size={20}
                                                        style={editstyle}
                                                        onClick={() => navigate('/services/create', { state: { id: el.id } })}
                                                    />
                                                    <RiDeleteBin6Fill
                                                        size={20}
                                                        className='ml-3'
                                                        style={deletstyle}
                                                        onClick={() => handleServiceDelete(el.id)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="py-4 text-center text-sm">No Services Available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mx-4 mb-4 rounded-md border border-gray-100">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            recordsPerPage={recordsPerPage}
                            onRecordsPerPageChange={(val) => {
                                setRecordsPerPage(val);
                                setCurrentPage(1);
                            }}
                            totalRecords={totalRecords}
                            count={displayedCount}
                            showFirstLastControls
                            compact
                        />
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default ListServices;
